import React, { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import prettyMilliseconds from "pretty-ms";
import { Dl, renderDlGroup } from "~/components/DescriptionList";
import { DrawerHeader } from "~/components/DrawerHeader";
import { Error } from "~/components/Error";
import { Time } from "~/components/Time";
import { useLogs } from "~/domain/datastores";
import { useDebouncedValue, useSessionStorage } from "~/hooks";
import {
  fromUnixTime,
  nanosecondsToMilliseconds,
  nanosecondsToSeconds,
} from "~/lib/dates";
import { invariant } from "~/lib/invariant";
import type { Log } from "~/services/datastore";
import { usePlayerConfig, usePlayerLog } from "../../hooks";

type RefetchReason = "input" | "previous" | "next";

const limit = 10;

const SEARCH_QUERY_STORAGE_KEY = "log-drawer-search-query";
const SEARCH_OFFSET_STORAGE_KEY = "log-drawer-search-offset";

export default function LogDrawer() {
  const { logId, setLogId } = usePlayerConfig();
  const currentLogQuery = usePlayerLog();

  const refetchReasonRef = useRef<RefetchReason>();
  const searchHeaderRef = useRef<HTMLParagraphElement | null>(null);

  const [search, setSearch] = useSessionStorage(SEARCH_QUERY_STORAGE_KEY, "");
  const [offset, setOffset] = useSessionStorage(SEARCH_OFFSET_STORAGE_KEY, 0);
  const debouncedSearch = useDebouncedValue(search, 200);
  const searchQuery = useLogs(
    {
      nameLike: debouncedSearch,
      offset,
      limit,
      sort: "desc",
      order: "start_time",
      startTimeNull: false,
      endTimeNull: false,
    },
    {
      keepPreviousData: true,
      staleTime: 0,
      cacheTime: 0,
    },
  );

  useEffect(
    function scrollToTopOfSearchOnPageChange() {
      if (
        searchQuery.data !== undefined &&
        (refetchReasonRef.current === "previous" ||
          refetchReasonRef.current === "next")
      ) {
        searchHeaderRef.current?.scrollIntoView(true);
      }
    },
    [searchQuery.data],
  );

  const isPaginationDisabled =
    !searchQuery.isSuccess || searchQuery.data.count === 0;

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    refetchReasonRef.current = "input";

    setSearch(e.target.value);
  }

  function makePaginationHandler(direction: "previous" | "next") {
    return function handlePaginationChange() {
      refetchReasonRef.current = direction;

      const change = direction === "previous" ? -limit : limit;
      setOffset(offset + change);
    };
  }

  function causedRefetch(reason: RefetchReason) {
    return searchQuery.isFetching && refetchReasonRef.current === reason;
  }

  return (
    <>
      <DrawerHeader title="Logs" />
      <Stack spacing={2}>
        <div>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" component="p">
              Current Log
            </Typography>
          </Stack>
          {logId == null ? (
            <Typography paragraph>
              No log selected. Search for one below
            </Typography>
          ) : currentLogQuery.isSuccess ? (
            <Dl
              spacing={3}
              sx={{
                mt: 0,
                "& .MuiGrid-root:first-child": {
                  pt: 0,
                },
              }}
            >
              {renderDlGroup("Name", currentLogQuery.data.name, { xs: 12 })}
              {renderDlGroup(
                "Start Time",
                currentLogQuery.data.startTime === null ? (
                  "--"
                ) : (
                  <Time
                    date={fromUnixTime(
                      nanosecondsToSeconds(currentLogQuery.data.startTime),
                    )}
                  />
                ),
                { xs: 12 },
              )}
              {renderDlGroup(
                "Duration",
                currentLogQuery.data.startTime === null ||
                  currentLogQuery.data.endTime === null
                  ? "--"
                  : renderLogDuration(currentLogQuery.data),
                { xs: 12 },
              )}
            </Dl>
          ) : currentLogQuery.isError ? (
            <Typography paragraph>
              An error occurred trying to load the log.
            </Typography>
          ) : (
            <Typography paragraph>Loading the log...</Typography>
          )}
        </div>
        <Divider />
        <div>
          <Typography
            ref={searchHeaderRef}
            variant="h6"
            component="p"
            gutterBottom
          >
            Search Logs
          </Typography>
          <Typography paragraph>
            Select a log from the search results below to play its records and
            create digestions of its topics
          </Typography>
          <TextField
            label="Search for logs"
            fullWidth
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: causedRefetch("input") ? (
                <CircularProgress size="1.5rem" />
              ) : undefined,
            }}
          />
          {searchQuery.isError ? (
            <Box my={3}>
              <Error>
                <Typography>An error occurred searching for logs</Typography>
              </Error>
            </Box>
          ) : (
            <>
              <List>
                {searchQuery.isSuccess && searchQuery.data.count === 0 ? (
                  <ListItem>No logs matched the search</ListItem>
                ) : searchQuery.isSuccess ? (
                  searchQuery.data.data.map((log) => {
                    const hasDefinedBounds =
                      log.startTime !== null && log.endTime !== null;

                    return (
                      <ListItem key={log.id} disablePadding>
                        <ListItemButton
                          selected={log.id === logId}
                          role={undefined}
                          onClick={() => setLogId(log.id)}
                        >
                          <ListItemText
                            disableTypography
                            secondary={
                              hasDefinedBounds ? (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  {...(hasDefinedBounds
                                    ? { fontWeight: "bold" }
                                    : { fontStyle: "italic" })}
                                >
                                  {renderLogDuration(log)} &bull;{" "}
                                  <Time
                                    date={fromUnixTime(
                                      nanosecondsToSeconds(log.startTime!),
                                    )}
                                  />
                                </Typography>
                              ) : (
                                "--"
                              )
                            }
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              width={1}
                              sx={{ wordBreak: "break-all" }}
                            >
                              <Typography fontWeight="bold">
                                {log.name}
                              </Typography>
                            </Stack>
                          </ListItemText>
                        </ListItemButton>
                      </ListItem>
                    );
                  })
                ) : (
                  <ListItem>Fetching logs...</ListItem>
                )}
              </List>
              <Stack direction="row" justifyContent="space-between">
                <LoadingButton
                  loading={causedRefetch("previous")}
                  startIcon={<ChevronLeft />}
                  disabled={isPaginationDisabled || offset === 0}
                  onClick={makePaginationHandler("previous")}
                >
                  Previous
                </LoadingButton>
                <LoadingButton
                  loading={causedRefetch("next")}
                  endIcon={<ChevronRight />}
                  disabled={
                    isPaginationDisabled ||
                    offset + limit >= searchQuery.data.count
                  }
                  onClick={makePaginationHandler("next")}
                >
                  Next
                </LoadingButton>
              </Stack>
            </>
          )}
        </div>
      </Stack>
    </>
  );
}

function renderLogDuration(log: Log) {
  const { startTime, endTime } = log;

  invariant(
    startTime !== null && endTime !== null,
    "Log must have defined bounds to render duration",
  );

  return prettyMilliseconds(nanosecondsToMilliseconds(endTime - startTime), {
    secondsDecimalDigits: 0,
  });
}
